import React, { useState, Dispatch, useEffect } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import { Box, FormControlLabel, Typography, Checkbox, CircularProgress, Button } from '@mui/material';
import Link from 'next/link';
import makeStyles from '@mui/styles/makeStyles';

import { Data, get } from '../../networking';
import SunnysideButton from '../../components_sunnyside/button';
import { useCookies } from 'react-cookie';
import useAppUserAgent from '../core/use-app-user-agent';
import { ADJUST_EVENTS, AdjustEventPayload, trackAdjustEvent } from '../../lib/adjust';

const DEFAULT_COUNTRY = 'us';

const getAuthVerify = async (phone): Promise<Data> => {
    return await get('auth/verify', { phone });
};

const getPhoneValidation = async (phone): Promise<Data> => {
    return await get('twilio/lookup', { phone });
};

const getUserCountry = async (): Promise<Data> => {
    return await get('users/country');
};

type Props = {
    mode: 'register' | 'login',
    initialPhone?: string,
    onChange?: (data: any) => void,
    // Prevents phone submit from continuing until function returns true
    canContinue?: () => boolean | Promise<boolean>,
    persistentLoginHook?: [boolean, Dispatch<any>],
    formButtonLabel?: string;
    buttonIcon?: any;
    disclaimer?: string;
    isCustom?: boolean;
};

interface CountryData {
    name: string;
    dialCode: string;
    countryCode: string;
    format: string;
}

export default function Phone(props: Props): JSX.Element {
    const useStyles = makeStyles(() => ({
        phoneContainer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'left',
            '& .form-control': {
                paddingLeft: '1rem',
                width: '100% !important',
            },
            '& .react-tel-input input.form-control, & .react-tel-input > div.flag-dropdown': {
                borderBottom: props.isCustom ? 'none' : ''
            },
            '& .react-tel-input input.form-control:focus': {
                boxShadow: props.isCustom ? 'none' : ''
            }
        },
        disclaimerBox: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            columnGap: '12px',
            marginTop: '16px'
        },
        disclaimer: {
            fontFamily: 'Poppins',
            fontSize: '10px',
            lineHeight: '16px',
            color: '#333',
            margin: 0
        },
        arrowButton: {
            position: 'relative',
            bottom: 0,
            '&:hover': {
                backgroundColor: '#FFE635',
            },
            backgroundColor: '#FFE635',
            padding: '16px',
            borderRadius: '50%',
            placeSelf: 'end',
            minWidth: 0,
            lineHeight: 0,
            transition: 'opacity 0.5s, pointerEvents 0.5s'
        }
    }));

    const [cookies] = useCookies();
    const [country, setCountry] = useState(DEFAULT_COUNTRY);
    const [phone, setPhone] = useState(props.initialPhone);
    const [countryCode, setCountryCode] = useState('1');
    const [persistentLogin, setPersistentLogin] = props.persistentLoginHook || useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState('');
    const appUserAgent = useAppUserAgent();

    const [renderInput, setRenderInput] = useState(false);
    useEffect(() => {
        setTimeout(() => {
            setRenderInput(true);
        }, 1000);
    }, []);

    const classes = useStyles();

    const phoneRegisteredError = 'Phone number already registered';
    const noAccountError = 'Phone number does not exist';

    const onKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>): void => {
        if (ev.key === 'Backspace') {
            // -1 from phone.length since phone contains '+'
            if (countryCode.length === (phone.length - 1) || !phone) {
                ev.preventDefault();
            }
        }
    };

    const handlePhoneChangeEv = (phoneValue: string, countryData: CountryData | { [key: string]: string; }): void => {
        if (countryData['dialCode'] !== countryCode) {
            setCountryCode(countryData.dialCode);
            if (countryData.countryCode !== country) setCountry(countryData.countryCode);
        }
        phoneValue = '+' + phoneValue.replace(/ /g, '');
        setPhone(phoneValue);
        setError('');
    };

    const handleClickSubmit = async (phoneNumber) => {
        if (props.canContinue && !(await props.canContinue())) {
            return;
        }
        if (!isProcessing) {
            setIsProcessing(true);
            const phoneNumberIsValid = await getPhoneValidation(phoneNumber);
            if (!phoneNumberIsValid.message && phoneNumberIsValid.phoneNumber) {
                const canRegister = await getAuthVerify(phoneNumber);
                if (props.mode === 'register') {
                    if (props.initialPhone !== phone && !canRegister.isValid) {
                        setError(phoneRegisteredError);
                    } else {
                        props.onChange(phone);

                        if (cookies?.ss_app === 'true' && (cookies?.ss_adid || cookies?.ss_idfv || cookies?.ss_gps_adid)) {
                            const event_token = ADJUST_EVENTS[cookies.ss_app_device]?.phone;
                            if (event_token) {
                                const payload: AdjustEventPayload = {
                                    event_token,
                                };

                                if (cookies.ss_adid) payload.adid = cookies.ss_adid;
                                if (cookies.ss_idfv) payload.idfv = cookies.ss_idfv;
                                if (cookies.ss_gps_adid) payload.gps_adid = cookies.ss_gps_adid;

                                trackAdjustEvent(payload, cookies.ss_app_device);
                            }
                        }
                    }
                } else if (props.mode === 'login') {
                    if (canRegister.isValid) {
                        setError(noAccountError);
                    } else {
                        props.onChange(phone);
                    }
                }
            } else {
                setError(phoneNumberIsValid.message as string || 'Please enter a valid phone number');
            }
            setIsProcessing(false);
        }
    };

    const handleFormSubmit = function (e) {
        e.preventDefault();
        handleClickSubmit(phone);
    };

    /* Dynamically sets default country but if user has already changed country no nothing */
    const handleGetUserCountry = async () => {
        const { country: userCountry } = await getUserCountry();
        if (country === DEFAULT_COUNTRY) setCountry((userCountry as string).toLowerCase());
    };

    const HandleErrorComponent = (): JSX.Element => {
        switch (error) {
        case phoneRegisteredError: {
            const url = () => {
                if (cookies?.ss_app === 'true' || appUserAgent?.isApp) {
                    return '/app-bridge?redirect=LoginStackNav';
                }
                return '/login/phone';
            };
            return (
                <Box component="span">
                    {error}<br></br>
                    <Link href={url()}>
                        <a>Login to your account <img src={'/img/btn-arrow-right-primary.png'} srcSet={'/img/btn-arrow-right-primary@2x.png 2x, /img/btn-arrow-right-primary@3x.png 3x'} /></a>
                    </Link>
                </Box>
            );
        }
        case noAccountError:
            return (
                <Box component="span">
                    {error}<br></br>
                    <Link href="/landing"><a>Start your free trial <img src={'/img/btn-arrow-right-primary.png'} srcSet={'/img/btn-arrow-right-primary@2x.png 2x, /img/btn-arrow-right-primary@3x.png 3x'} /></a></Link>
                </Box>
            );
        default:
            return <>{error}</>;
        }
    };

    useEffect(() => {
        handleGetUserCountry();
    }, []);

    const onChange = (phone, data) => handlePhoneChangeEv(phone, data);
    const onCheckChange = () => setPersistentLogin(!persistentLogin);
    const onButtonClick = () => handleClickSubmit(phone);

    return (
        <div className={classes.phoneContainer} style={{ height: props.isCustom ? '100%' : 'auto' }}>
            <Box width='100%' mt={props.isCustom ? 0 : '1rem'} mb={props.isCustom ? 0 : '1rem'}>
                {props.isCustom && !renderInput && (
                    <form id="phoneForm" onSubmit={handleFormSubmit}>
                        <PhoneInput
                            specialLabel=''
                            value={phone}
                            country={country}
                            onChange={onChange}
                            onEnterKeyPress={handleFormSubmit}
                            onKeyDown={onKeyDown}
                            preferredCountries={['ca', 'us', 'gb']}
                            containerStyle={{
                                display: 'flex',
                                width: '100%',
                                outline: props.isCustom ? '1px solid #EBEBEB' : '',
                                borderRadius: props.isCustom ? '12px' : ''
                            }}
                            inputProps={{
                                autoFocus: false
                            }}
                        />
                    </form>
                )}
                {(!props.isCustom || renderInput) && (
                    <form id="phoneForm" onSubmit={handleFormSubmit}>
                        <PhoneInput
                            specialLabel=''
                            value={phone}
                            country={country}
                            onChange={onChange}
                            onEnterKeyPress={handleFormSubmit}
                            onKeyDown={onKeyDown}
                            preferredCountries={['ca', 'us', 'gb']}
                            containerStyle={{
                                display: 'flex',
                                width: '100%',
                                outline: props.isCustom ? '1px solid #EBEBEB' : '',
                                borderRadius: props.isCustom ? '12px' : ''
                            }}
                            inputProps={{
                                autoFocus: true
                            }}
                        />
                    </form>
                )}
                {props.isCustom && (
                    <Box className={classes.disclaimerBox}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" clipRule="evenodd" d="M3.258 7.67158C2.84115 7.67158 2.50322 8.00951 2.50322 8.42636V13.8455C2.50322 14.2623 2.84115 14.6002 3.258 14.6002H12.7414C13.1583 14.6002 13.4962 14.2623 13.4962 13.8455V8.42636C13.4962 8.00951 13.1583 7.67158 12.7414 7.67158H3.258ZM1.30322 8.42636C1.30322 7.34676 2.17841 6.47158 3.258 6.47158H12.7414C13.821 6.47158 14.6962 7.34676 14.6962 8.42636V13.8455C14.6962 14.9251 13.821 15.8002 12.7414 15.8002H3.258C2.17841 15.8002 1.30322 14.9251 1.30322 13.8455V8.42636Z" fill="#333333" />
                            <path fillRule="evenodd" clipRule="evenodd" d="M7.99784 10.1521C7.45457 10.1521 7.01416 10.5925 7.01416 11.1357C7.01416 11.679 7.45457 12.1194 7.99784 12.1194C8.54111 12.1194 8.98152 11.679 8.98152 11.1357C8.98152 10.5925 8.54111 10.1521 7.99784 10.1521ZM5.81416 11.1357C5.81416 9.92972 6.79183 8.95205 7.99784 8.95205C9.20386 8.95205 10.1815 9.92972 10.1815 11.1357C10.1815 12.3417 9.20386 13.3194 7.99784 13.3194C6.79183 13.3194 5.81416 12.3417 5.81416 11.1357Z" fill="#333333" />
                            <path fillRule="evenodd" clipRule="evenodd" d="M8.00396 0.199873H8.07499C9.12333 0.210621 10.1245 0.637329 10.8583 1.3861C11.5913 2.13414 11.9976 3.14244 11.988 4.18967V4.8642C11.988 5.19557 11.7194 5.4642 11.388 5.4642C11.0567 5.4642 10.788 5.19557 10.788 4.8642V4.18084C10.7953 3.45068 10.5123 2.74753 10.0012 2.22602C9.49082 1.7052 8.7947 1.40812 8.06561 1.39987H7.99513C7.26497 1.39261 6.56182 1.67561 6.0403 2.18669C5.51949 2.69709 5.22241 3.39321 5.21416 4.1223V4.8642C5.21416 5.19557 4.94553 5.4642 4.61416 5.4642C4.28279 5.4642 4.01416 5.19557 4.01416 4.8642V4.11292C4.02491 3.06458 4.45162 2.06344 5.20039 1.32964C5.94843 0.596563 6.95673 0.190276 8.00396 0.199873Z" fill="#333333" />
                        </svg>
                        <em><p className={classes.disclaimer}>{'We\'ll never share your number with any third parties'}</p></em>
                    </Box>
                )}
                <Typography variant="caption" color="error">
                    <HandleErrorComponent />
                </Typography>
                {props.mode === 'login' && props.persistentLoginHook && (
                    <Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={persistentLogin}
                                    onChange={onCheckChange}
                                    size="small"
                                />
                            }
                            label="Keep me logged in"
                        />
                    </Box>
                )}
            </Box>
            {!props.isCustom ? (
                <SunnysideButton
                    width={props.disclaimer ? '100%' : '350px'}
                    borderRadius='59.631px'
                    className={!props.disclaimer && 'button-fixed-width'}
                    disableElevation
                    onClick={onButtonClick}
                    fontSize='1.1rem'
                    startIcon={props.buttonIcon}>
                    {isProcessing ? (
                        <CircularProgress color='inherit' />
                    ) : (
                        props.formButtonLabel || 'Continue'
                    )}
                </SunnysideButton>
            ) : (
                <>
                    <Box style={{ height: '64px' }} />
                    <Button className={classes.arrowButton} style={{ opacity: phone.slice(countryCode.length + 1, phone.length).length ? 1 : 0, pointerEvents: phone.slice(countryCode.length + 1, phone.length).length ? 'auto' : 'none' }} onClick={onButtonClick}>
                        <svg width="16" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8.70503 14.095C8.43166 13.8216 8.43166 13.3784 8.70503 13.105L13.1101 8.69999L2.2 8.69999C1.8134 8.69999 1.5 8.38659 1.5 7.99999C1.5 7.61339 1.8134 7.29999 2.2 7.29999L13.1101 7.29999L8.70503 2.89496C8.43166 2.62159 8.43166 2.17838 8.70503 1.90501C8.97839 1.63164 9.42161 1.63164 9.69498 1.90501L15.295 7.50501C15.5683 7.77838 15.5683 8.22159 15.295 8.49496L9.69497 14.095C9.42161 14.3683 8.97839 14.3683 8.70503 14.095Z" fill={'black'} />
                        </svg>
                    </Button>
                </>
            )}
        </div>
    );
}
